import type { EvaluationScore } from "@chessla/shared";
import type { AnalyzedMoveResult } from "@/shared/api/engine.api";
import { normalizeEvaluation } from "@/shared/components/evaluation";

export interface GameEvidencePoint {
  index: number;
  moveNumber: number;
  san: string;
  evaluation: EvaluationScore | null;
  thinkSeconds: number | null;
  moverColor: "white" | "black";
  isPlayerMove: boolean;
  motifs: string[];
}

const hiddenTimelineMotifs = new Set(["imprecision", "opponent_imprecision", "book"]);

export const buildGameEvidenceTimeline = (
  moves: AnalyzedMoveResult[],
  includeOpponentEvidence = false,
): GameEvidencePoint[] =>
  moves.map((move, index) => {
    const next = moves[index + 1];
    const raw =
      move.evalAfterCp != null || move.evalAfterMate != null
        ? { cp: move.evalAfterMate == null ? move.evalAfterCp : null, mate: move.evalAfterMate }
        : next?.fenBefore === move.fenAfter && (next.evalBeforeCp != null || next.evalBeforeMate != null)
          ? { cp: next.evalBeforeMate == null ? next.evalBeforeCp : null, mate: next.evalBeforeMate }
          : null;
    const side = move.fenAfter.split(" ")[1] === "b" ? "b" : "w";
    return {
      index,
      moveNumber: move.moveNumber,
      san: move.san,
      evaluation: normalizeEvaluation(raw, side),
      thinkSeconds: move.timeSpentSeconds,
      moverColor: move.fenBefore.split(" ")[1] === "b" ? "black" : "white",
      isPlayerMove: move.isPlayerMove,
      motifs:
        move.isPlayerMove || includeOpponentEvidence
          ? [
              ...new Set([
                ...(move.motifs ?? []),
                ...(move.tacticalFindings ?? [])
                  .filter(({ confidence }) => confidence === "verified")
                  .map(({ motif }) => motif),
              ]),
            ].filter((motif) => !hiddenTimelineMotifs.has(motif))
          : [],
    };
  });
